import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Calendar, TruckIcon, CreditCard, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

export default function UserOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [apiError, setApiError] = useState("");
  const [expandedOrders, setExpandedOrders] = useState({});
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Get email from Auth0 data in localStorage
  const [userEmail, setUserEmail] = useState("");
  
  useEffect(() => {
    // Extract email from localStorage Auth0 data
    try {
      const auth0DataKeys = Object.keys(localStorage).filter(key => 
        key.startsWith("@@auth0spajs@@")
      );
      
      if (auth0DataKeys.length > 0) {
        const auth0Key = auth0DataKeys.find(key => key.includes("@@user@@"));
        if (auth0Key) {
          const userData = JSON.parse(localStorage.getItem(auth0Key));
          if (userData && userData.decodedToken && userData.decodedToken.claims && userData.decodedToken.claims.email) {
            setUserEmail(userData.decodedToken.claims.email);
          }
        }
      }
    } catch (error) {
      console.error("Error getting email from localStorage:", error);
    }
  }, []);
  
  // Fetch orders when userEmail is available
  useEffect(() => {
    const fetchOrders = async () => {
      if (!userEmail) return;
      
      setLoadingOrders(true);
      try {
        const response = await fetch(`${API_BASE_URL}/list-user-order/${encodeURIComponent(userEmail)}/`);
        if (!response.ok) {
          throw new Error("Failed to fetch orders");
        }
        
        const data = await response.json();
        if (data.success) {
          setOrders(data.orders);
        } else {
          throw new Error(data.message || "Failed to load orders");
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
        setApiError("Unable to load your orders. Please try again later.");
      } finally {
        setLoadingOrders(false);
      }
    };
    
    if (userEmail) {
      fetchOrders();
    }
  }, [userEmail]);
  
  const toggleOrderExpand = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };
  
  // Format date from ISO string
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Status badge component with appropriate colors
  const StatusBadge = ({ status }) => {
    let bgColor = "bg-gray-100";
    let textColor = "text-gray-800";
    
    switch(status.toLowerCase()) {
      case "delivered":
        bgColor = "bg-green-100";
        textColor = "text-green-800";
        break;
      case "shipped":
        bgColor = "bg-blue-100";
        textColor = "text-blue-800";
        break;
      case "processing":
        bgColor = "bg-yellow-100";
        textColor = "text-yellow-800";
        break;
      case "cancelled":
        bgColor = "bg-red-100";
        textColor = "text-red-800";
        break;
      case "pending":
        bgColor = "bg-purple-100";
        textColor = "text-purple-800";
        break;
      default:
        break;
    }
    
    return (
      <span className={`${bgColor} ${textColor} text-xs font-medium px-2.5 py-1 rounded-sm uppercase`}>
        {status}
      </span>
    );
  };

  return (
    <>
      {/* Inline CSS for animations */}
      <style>
        {`
          @keyframes fadeInUp {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeInUp {
            animation: fadeInUp 1s ease-out forwards;
          }
          .order-item:hover {
            box-shadow: 0 4px 12px rgba(212, 175, 55, 0.15);
          }
        `}
      </style>
      <section className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white px-4 md:px-8">
        <div className="max-w-4xl w-full bg-white rounded-sm shadow-lg p-8 animate-fadeInUp">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl text-[#333333] mb-2 tracking-wide">
              Your Orders
            </h2>
            <p className="text-sm text-gray-500">
              View and track all your purchases
            </p>
            <div className="mt-4 h-1 w-20 bg-[#D4AF37] mx-auto rounded-full"></div>
          </div>
          
          {/* Error Message */}
          {apiError && (
            <div className="mb-6 p-3 bg-red-100 text-red-700 rounded-sm text-sm">
              {apiError}
            </div>
          )}
          
          {/* Loading State */}
          {!userEmail || loadingOrders ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-10 w-10 text-[#D4AF37] animate-spin mb-4" />
              <p className="text-gray-600">Loading your orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-10 text-center">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No orders found</h3>
              <p className="text-gray-500 mb-6">You haven't placed any orders yet.</p>
              <button 
                onClick={() => navigate("/")}
                className="bg-[#D4AF37] text-black py-2 px-6 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200 transform hover:scale-105"
              >
                Start Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Orders Count */}
              <div className="text-sm text-gray-700 font-medium mb-4">
                Showing {orders.length} {orders.length === 1 ? 'order' : 'orders'}
              </div>
              
              {/* Orders List */}
              {orders.map((order) => (
                <div 
                  key={order.order_id}
                  className="border border-gray-200 rounded-sm overflow-hidden order-item transition-all duration-300"
                >
                  {/* Order Header */}
                  <div className="bg-gray-50 p-4 border-b border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div className="mb-3 md:mb-0">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                        <Package className="h-4 w-4 text-[#D4AF37]" />
                        <span>Order #{order.order_id.substring(0, 8)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        <span>Placed on {formatDate(order.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                      <StatusBadge status={order.status} />
                      <div className="text-sm font-medium">
                        ₹{order.order_summary.finalTotal.toFixed(2)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Order Summary */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-start gap-4">
                      {/* Order first item image */}
                      {order.cart_items[0] && (
                        <div className="w-20 h-20 flex-shrink-0">
                          <img 
                            src={order.cart_items[0].image} 
                            alt={order.cart_items[0].name}
                            className="w-full h-full object-cover rounded-sm"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/api/placeholder/80/80";
                            }}
                          />
                        </div>
                      )}
                      
                      {/* Order items summary */}
                      <div className="flex-grow">
                        <div className="text-sm font-medium">
                          {order.cart_items[0] ? order.cart_items[0].name : "Product"} 
                          {order.cart_items.length > 1 && 
                            <span className="text-gray-500 font-normal"> + {order.cart_items.length - 1} more item{order.cart_items.length - 1 > 1 ? 's' : ''}</span>
                          }
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Quantity: {order.cart_items.reduce((total, item) => total + item.quantity, 0)} items
                        </div>
                        
                        {/* Shipped to info */}
                        <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                          <TruckIcon className="h-3 w-3" />
                          <span>Shipped to: {order.shipping_info.city}, {order.shipping_info.state}</span>
                        </div>
                        
                        {/* Payment method */}
                        <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                          <CreditCard className="h-3 w-3" />
                          <span>Payment: {order.billing_info.paymentMethod}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* View Details Button */}
                  <button 
                    onClick={() => toggleOrderExpand(order.order_id)}
                    className="w-full p-3 text-sm flex items-center justify-center gap-2 text-[#333333] hover:bg-gray-50 transition-colors duration-200"
                  >
                    {expandedOrders[order.order_id] ? (
                      <>Hide Details <ChevronUp className="h-4 w-4" /></>
                    ) : (
                      <>View Details <ChevronDown className="h-4 w-4" /></>
                    )}
                  </button>
                  
                  {/* Expanded Order Details */}
                  {expandedOrders[order.order_id] && (
                    <div className="p-4 bg-gray-50 animate-fadeInUp">
                      {/* Order Items */}
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-[#333333] mb-3">Order Items</h4>
                        <div className="space-y-4">
                          {order.cart_items.map((item) => (
                            <div key={item.cart_id} className="flex items-start gap-3 pb-3 border-b border-gray-200 last:border-0">
                              {/* Product Image */}
                              <div className="w-16 h-16 flex-shrink-0">
                                <img 
                                  src={item.image} 
                                  alt={item.name}
                                  className="w-full h-full object-cover rounded-sm"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "/api/placeholder/64/64";
                                  }}
                                />
                              </div>
                              
                              {/* Product Details */}
                              <div className="flex-grow">
                                <div className="text-sm font-medium">{item.name}</div>
                                <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-gray-500">
                                  <div>Size: {item.size}</div>
                                  <div>Quantity: {item.quantity}</div>
                                  <div>Price: ₹{item.unit_price.toFixed(2)}</div>
                                  <div>Total: ₹{item.total_price.toFixed(2)}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Order Summary and Shipping Details */}
                      <div className="grid md:grid-cols-2 gap-6">
                        {/* Order Summary */}
                        <div>
                          <h4 className="text-sm font-medium text-[#333333] mb-3">Order Summary</h4>
                          <div className="bg-white p-4 rounded-sm border border-gray-200">
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Subtotal:</span>
                                <span>₹{order.order_summary.subtotal.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Shipping:</span>
                                <span>₹{order.order_summary.shippingCost.toFixed(2)}</span>
                              </div>
                              {order.order_summary.discount > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Discount:</span>
                                  <span className="text-green-600">-₹{order.order_summary.discount.toFixed(2)}</span>
                                </div>
                              )}
                              <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between font-medium">
                                <span>Total:</span>
                                <span>₹{order.order_summary.finalTotal.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Shipping Details */}
                        <div>
                          <h4 className="text-sm font-medium text-[#333333] mb-3">Shipping Details</h4>
                          <div className="bg-white p-4 rounded-sm border border-gray-200">
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="text-gray-600">Name:</span>
                                <span className="ml-2">{order.billing_info.fullName}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Phone:</span>
                                <span className="ml-2">{order.billing_info.phone}</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Address:</span>
                                <div className="ml-2">
                                  {order.shipping_info.address},
                                  <br />
                                  {order.shipping_info.city}, {order.shipping_info.state},
                                  <br />
                                  {order.shipping_info.zip}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Back Button */}
          <div className="mt-8 text-center">
            <button 
              onClick={() => navigate("/")}
              className="bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-black py-2 px-8 rounded-sm text-sm font-medium hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
